import { useState, type FormEvent } from "react";

import { customMetrics, metrics, type CustomMetric, type CustomMetricInput } from "../lib/api.js";
import { MAX_LINES, formatValue, rankSeries } from "../lib/custom-metrics.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useSeries } from "../lib/use-series.js";
import { CONTROL_ROW } from "./control-style.js";
import { LineChart, type ChartSeries } from "./LineChart.js";
import { MibPicker } from "./MibPicker.js";
import { RangePicker } from "./RangePicker.js";

/**
 * Metrics an operator defined for this device, one chart each.
 *
 * Shown for every device that has any, and to anyone who can read the device
 * -- including organizations it is shared with. Only someone who may edit it
 * sees the controls to add or remove one.
 */

const COLORS = ["var(--series-1)", "var(--series-2)", "var(--series-3)", "var(--series-4)", "var(--series-5)", "var(--series-6)"];

export function CustomMetricsSection({ deviceId, canWrite }: { deviceId: string; canWrite: boolean }) {
  const { data, reload } = useLiveData(() => customMetrics.list(deviceId), 60_000, [deviceId]);
  const [hours, setHours] = useState(24);
  // By hand (an OID typed in) or from the MIB library; one form at a time.
  const [adding, setAdding] = useState<"oid" | "mib" | null>(null);
  const list = (data?.metrics ?? []).filter((m) => m.enabled);

  // Nothing to show and nothing the viewer could add: say nothing at all.
  if (list.length === 0 && !canWrite) return null;

  return (
    <section style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={title}>&gt; CUSTOM METRICS</h2>
        <div style={CONTROL_ROW}>
          {list.length > 0 && <RangePicker hours={hours} onChange={setHours} />}
          {canWrite && !adding && (
            <>
              <button onClick={() => setAdding("mib")} style={button}>[ + FROM MIB ]</button>
              <button onClick={() => setAdding("oid")} style={button}>[ + BY OID ]</button>
            </>
          )}
        </div>
      </div>

      {adding === "oid" && (
        <AddMetricForm
          deviceId={deviceId}
          onDone={() => {
            setAdding(null);
            reload();
          }}
        />
      )}
      {adding === "mib" && (
        <MibPicker
          deviceId={deviceId}
          onDone={() => {
            setAdding(null);
            reload();
          }}
        />
      )}

      {list.length === 0 && !adding && (
        <p style={help}>
          None yet. Add one from a MIB or by OID.
        </p>
      )}

      <div style={{ display: "grid", gap: 22 }}>
        {list.map((m) => (
          <MetricChart key={m.id} metric={m} hours={hours} canWrite={canWrite} onChange={reload} />
        ))}
      </div>
    </section>
  );
}

function MetricChart({
  metric,
  hours,
  canWrite,
  onChange,
}: {
  metric: CustomMetric;
  hours: number;
  canWrite: boolean;
  onChange: () => void;
}) {
  const { data, loading } = useSeries((r) => metrics.custom(metric.id, r), hours, [metric.id]);
  const [showAll, setShowAll] = useState(false);
  const format = (v: number) => formatValue(v, metric.unit);

  const ranked = rankSeries(data?.series ?? []);
  const toChart = (s: (typeof ranked)[number], i: number): ChartSeries => ({
    key: s.key || "value",
    label: s.label ?? (s.key ? `row ${s.key}` : metric.name),
    color: COLORS[i % COLORS.length]!,
    format,
    points: s.points.map((p) => ({ x: new Date(p.t).getTime(), y: p.value })),
  });
  const hidden = Math.max(0, ranked.length - MAX_LINES);

  const remove = async () => {
    if (!confirm(`Stop collecting "${metric.name}" and delete its history?`)) return;
    await customMetrics.remove(metric.id);
    onChange();
  };

  return (
    <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={subLabel}>
          {metric.name}
          {metric.unit && <span style={{ color: "var(--text-muted)" }}> · {metric.unit}</span>}
          {metric.kind === "counter" && <span style={{ color: "var(--text-muted)" }}> · rate</span>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <span style={{ color: "var(--text-muted)", fontSize: 10 }}>
            {metric.mib_object ? `${metric.mib_module} · ${metric.mib_object}` : metric.oid}
          </span>
          {canWrite && (
            <button onClick={() => void remove()} style={{ ...button, padding: "1px 6px", color: "var(--status-critical)" }} title="Stop collecting and delete history">
              x
            </button>
          )}
        </div>
      </div>

      {showAll && ranked.length > MAX_LINES ? (
        // Small multiples rather than one chart of every row: past six lines
        // a single chart stops being readable for anyone.
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
          {ranked.map((s) => (
            <div key={s.key}>
              <div style={{ ...subLabel, marginBottom: 2 }}>{s.label ?? `row ${s.key}`}</div>
              <LineChart series={[{ ...toChart(s, 0), color: COLORS[0]! }]} height={110} yTickFormat={format} resolutionNote={data?.resolution} />
            </div>
          ))}
        </div>
      ) : (
        <LineChart
          series={ranked.slice(0, MAX_LINES).map(toChart)}
          yTickFormat={format}
          resolutionNote={data?.resolution}
          emptyMessage={data && ranked.length === 0 ? "Waiting for the first poll." : undefined}
        />
      )}

      {hidden > 0 && (
        <button onClick={() => setShowAll((v) => !v)} style={{ ...button, marginTop: 6 }}>
          {showAll ? "[ BUSIEST SIX ON ONE CHART ]" : `[ SHOW ALL ${ranked.length} ROWS ]`}
        </button>
      )}
      {hidden > 0 && !showAll && (
        <span style={{ ...help, marginLeft: 8 }}>
          The six busiest of {ranked.length} rows. {hidden} quieter ones are not drawn.
        </span>
      )}
    </div>
  );
}

function AddMetricForm({ deviceId, onDone }: { deviceId: string; onDone: () => void }) {
  const [form, setForm] = useState<CustomMetricInput>({ name: "", oid: "", is_table: false, kind: "gauge", scale: 1, unit: null });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof CustomMetricInput>(k: K, v: CustomMetricInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await customMetrics.create(deviceId, { ...form, label_oid: form.is_table ? form.label_oid || null : null });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} style={{ border: "1px solid var(--border)", padding: 12, margin: "12px 0", display: "grid", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <label style={field}>NAME<input required value={form.name} onChange={(e) => set("name", e.target.value)} style={input} /></label>
        <label style={{ ...field, flex: 2 }}>
          OID
          <input required value={form.oid} onChange={(e) => set("oid", e.target.value)} placeholder="1.3.6.1.4.1.…" style={input} />
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={field}>
          TYPE
          <select value={form.kind} onChange={(e) => set("kind", e.target.value as "gauge" | "counter")} style={input}>
            <option value="gauge">GAUGE — PLOT AS READ</option>
            <option value="counter">COUNTER — PLOT AS A RATE</option>
          </select>
        </label>
        <label style={field}>
          SHAPE
          <select value={form.is_table ? "table" : "scalar"} onChange={(e) => set("is_table", e.target.value === "table")} style={input}>
            <option value="scalar">ONE VALUE</option>
            <option value="table">TABLE COLUMN — ONE LINE PER ROW</option>
          </select>
        </label>
        {form.is_table && (
          <label style={{ ...field, flex: 2 }}>
            ROW LABEL OID (OPTIONAL)
            <input value={form.label_oid ?? ""} onChange={(e) => set("label_oid", e.target.value || null)} placeholder="a column naming each row" style={input} />
          </label>
        )}
        <label style={{ ...field, maxWidth: 90 }}>
          SCALE
          <input type="number" step="any" value={form.scale ?? 1} onChange={(e) => set("scale", Number(e.target.value))} style={input} />
        </label>
        <label style={{ ...field, maxWidth: 90 }}>
          UNIT
          <input value={form.unit ?? ""} onChange={(e) => set("unit", e.target.value || null)} placeholder="%, V, bit/s" style={input} />
        </label>
      </div>
      <p style={{ ...help, margin: 0 }}>
        Leave scale at 1 unless the device reports in tenths or similar. Single values usually end in .0.
      </p>
      {error && <p style={{ color: "var(--status-critical)", fontSize: 12, margin: 0 }}>{`!! ${error}`}</p>}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={busy} style={button}>[ ADD ]</button>
        <button type="button" onClick={onDone} style={button}>[ CANCEL ]</button>
      </div>
    </form>
  );
}

const card: React.CSSProperties = { background: "var(--surface-1)", border: "1px solid var(--border)", padding: 20, marginBottom: 16 };
const title: React.CSSProperties = { fontSize: 14, margin: 0, letterSpacing: "0.12em" };
const subLabel: React.CSSProperties = { fontSize: 11, color: "var(--text-secondary)", marginBottom: 6, letterSpacing: "0.1em", textTransform: "uppercase" };
const help: React.CSSProperties = { color: "var(--text-muted)", fontSize: 11, lineHeight: 1.5 };
const field: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 3, fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.08em", flex: 1, minWidth: 140 };
const input: React.CSSProperties = { padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", background: "var(--page-plane)", color: "var(--text-primary)", fontFamily: "inherit" };
const button: React.CSSProperties = { background: "none", border: "1px solid var(--border)", padding: "5px 9px", fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em", whiteSpace: "nowrap" };
